// Thin fetch wrapper. Two things it exists to centralise:
//
//   1. `credentials: "same-origin"` on every call — the session is an httpOnly cookie, and a
//      single forgotten option means a silently unauthenticated request.
//   2. 401 handling. A 401 is never a data error here; it means the session expired, and every
//      caller would otherwise have to translate it into "show the login page" itself.

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
  ) {
    super(message);
  }

  get isUnauthenticated() {
    return this.status === 401;
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  let response: Response;
  try {
    response = await fetch(`/api${path}`, {
      ...init,
      credentials: "same-origin",
      headers: { "Content-Type": "application/json", ...(init?.headers ?? {}) },
    });
  } catch {
    // Network-level failure: no response at all. Status 0 keeps callers from having to
    // distinguish a thrown TypeError from an HTTP error.
    throw new ApiError(0, "network");
  }

  if (!response.ok) {
    let detail = response.statusText;
    try {
      const body = await response.json();
      if (typeof body?.detail === "string") detail = body.detail;
    } catch {
      // Non-JSON error body (an nginx 502 page, say) — the status line is all there is.
    }
    throw new ApiError(response.status, detail);
  }

  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}

export const api = {
  get: <T>(path: string) => request<T>(path),
  post: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: "POST", body: body === undefined ? undefined : JSON.stringify(body) }),
  patch: <T>(path: string, body: unknown) =>
    request<T>(path, { method: "PATCH", body: JSON.stringify(body) }),
  del: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: "DELETE", body: body === undefined ? undefined : JSON.stringify(body) }),
};

export type AuthStatus = { authenticated: boolean; username: string | null };
export type User = { id: number; username: string };

export type TrackResponse = { hex: string; minutes: number; points: import("../map/types").TrackPoint[] };
export type ProjectionResponse = {
  hex: string;
  horizon: number;
  points: import("../map/types").ProjectionPoint[];
};

export const aircraft = {
  /** Everything the server is tracking, not just the streamed viewport — what search needs. */
  all: () => api.get<{ aircraft: import("../map/types").Aircraft[]; tracked: number }>("/aircraft"),
  detail: (hex: string) => api.get<import("../map/types").AircraftDetail>(`/aircraft/${hex}`),
  outlook: (hex: string) => api.get<{ outlook: import("../map/types").Outlook[] }>(`/aircraft/${hex}/outlook`),
  route: (hex: string) =>
    api.get<{ route: import("../map/types").FlightRoute | null }>(`/aircraft/${hex}/route`),
  photo: (hex: string) =>
    api.get<{ photo: import("../map/types").AircraftPhoto | null }>(`/aircraft/${hex}/photo`),
  track: (hex: string) => api.get<TrackResponse>(`/aircraft/${hex}/track`),
  projection: (hex: string) => api.get<ProjectionResponse>(`/aircraft/${hex}/projection`),
};

export type Settings = {
  home_lat: number | null;
  home_lon: number | null;
  home_label: string | null;
  home_prompt_dismissed: boolean;
  live_lat: number | null;
  live_lon: number | null;
  live_updated_at: string | null;
  live_is_fresh: boolean;
  live_override_ttl_seconds: number;
  auto_mute_threshold: number;
  auto_mute_mode: "suggest" | "enforce";
  units: "aviation" | "metric";
  quiet_hours_start: string | null;
  quiet_hours_end: string | null;
};

export type AlertRule = {
  id: number;
  name: string;
  enabled: boolean;
  anchor_mode: "auto" | "home" | "live" | "fixed";
  center_lat: number | null;
  center_lon: number | null;
  radius_m: number;
  alt_min_ft: number;
  alt_max_ft: number;
  lead_time_seconds: number;
  cooldown_minutes: number;
  respect_quiet_hours: boolean;
  daylight_only: boolean;
  min_size: MinSize;
  created_at: string;
  /** Why the rule cannot currently fire, in words meant to be shown verbatim. */
  dormant_reason: string | null;
  /** Silent by choice right now ("night", "quiet hours"), and when it wakes if known. */
  sleep_reason: string | null;
  wakes_at: string | null;
  last_notified_at: string | null;
  notified_last_7d: number;
};

export type MinSize = "any" | "large" | "heavy" | "four_engine";

export type RuleDraft = Omit<
  AlertRule,
  "id" | "created_at" | "dormant_reason" | "sleep_reason" | "wakes_at" | "last_notified_at" | "notified_last_7d"
>;

export type RecentAlert = {
  id: number;
  aircraft_hex: string;
  callsign: string | null;
  rule_name: string | null;
  icao_type: string | null;
  description: string | null;
  first_seen_at: string;
  notified_at: string | null;
  closest_distance_m: number | null;
  closest_alt_ft: number | null;
};

export const settings = {
  get: () => api.get<Settings>("/settings"),
  patch: (patch: Partial<Settings> & { clear_home?: boolean; clear_quiet_hours?: boolean }) =>
    api.patch<Settings>("/settings", patch),
  livePosition: (lat: number, lon: number) =>
    api.post<Settings>("/settings/live-position", { lat, lon }),
};

export const rules = {
  list: () => api.get<AlertRule[]>("/rules"),
  create: (draft: Partial<RuleDraft> & { name: string }) => api.post<AlertRule>("/rules", draft),
  update: (id: number, patch: Partial<RuleDraft>) => api.patch<AlertRule>(`/rules/${id}`, patch),
  remove: (id: number) => api.del<void>(`/rules/${id}`),
  test: (draft: Partial<RuleDraft> & { name: string }) =>
    api.post<{ delivered: number; pruned: number; aircraft: string | null; sample: boolean }>("/rules/test", draft),
  preview: (filters: { min_size: MinSize; alt_min_ft: number; alt_max_ft: number }) =>
    api.post<{ tracked: number; matching: number }>("/rules/preview", filters),
};

export type PushDevice = {
  id: number;
  user_agent: string | null;
  created_at: string;
  last_success_at: string | null;
  failure_count: number;
};

export const push = {
  config: () => api.get<{ enabled: boolean; vapid_public_key: string | null }>("/push/config"),
  subscribe: (subscription: PushSubscriptionJSON) =>
    api.post<{ id: number; created: boolean }>("/push/subscriptions", subscription),
  unsubscribe: (endpoint: string) => api.del<void>("/push/subscriptions", { endpoint }),
  test: () => api.post<{ delivered: number; pruned: number; message?: string }>("/push/test"),
  devices: () => api.get<{ subscriptions: PushDevice[] }>("/push/subscriptions"),
  currentDevice: (endpoint: string) => api.post<{ id: number }>("/push/subscriptions/current", { endpoint }),
  removeDevice: (id: number) => api.del<void>(`/push/subscriptions/${id}`),
};

export type TypeMute = {
  icao_type: string;
  description: string | null;
  muted_until: string | null;
  reason: "manual" | "auto_seen_count";
  created_at: string;
  seen_count: number;
};

export type MuteSuggestion = {
  icao_type: string;
  description: string | null;
  seen_count: number;
  threshold: number;
};

export type TypeSummary = {
  icao_type: string | null;
  description: string | null;
  seen_count: number;
  notified_count: number;
  last_seen_at: string | null;
  muted: boolean;
};

export const mutes = {
  list: () => api.get<TypeMute[]>("/mutes"),
  suggestions: () => api.get<MuteSuggestion[]>("/mutes/suggestions"),
  add: (icao_type: string, hours?: number) => api.post<TypeMute>("/mutes", { icao_type, hours }),
  remove: (icao_type: string) => api.del<void>(`/mutes/${icao_type}`),
};

export const sightings = {
  summary: () => api.get<TypeSummary[]>("/sightings/summary"),
  recentAlerts: (limit = 20) => api.get<RecentAlert[]>(`/sightings?notified_only=true&limit=${limit}`),
};

export const auth = {
  status: () => api.get<AuthStatus>("/auth/status"),
  login: (username: string, password: string) => api.post<User>("/auth/login", { username, password }),
  logout: () => api.post<{ ok: boolean }>("/auth/logout"),
};
